/**
 * Build client Select/Autocomplete options that disambiguate duplicate display names
 * with email, and keep values keyed by patient id.
 */

export type ClientSelectSource = {
  id: string;
  name: string;
  email?: string;
};

export type ClientSelectOption = {
  value: string;
  label: string;
};

/** Truncate for single-line select chips/options; full string stays on Tooltip. */
export function truncateClientSelectLabel(label: string, maxChars = 42): string {
  const trimmed = label.trim();
  if (trimmed.length <= maxChars) return trimmed;
  return `${trimmed.slice(0, Math.max(1, maxChars - 1))}…`;
}

/**
 * When multiple clients share a display name, append ` (email)` to the label.
 * `value` is always the client id.
 */
export function buildClientSelectOptions(clients: ClientSelectSource[]): ClientSelectOption[] {
  const nameCounts = new Map<string, number>();
  for (const client of clients) {
    const name = client.name.trim() || client.id;
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  }

  return clients
    .map((client) => {
      const name = client.name.trim() || client.id;
      const email = client.email?.trim();
      const needsEmail = (nameCounts.get(name) ?? 0) > 1 && Boolean(email);
      const label = needsEmail ? `${name} (${email})` : name;
      return { value: client.id, label };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Display name from a label built above, dropping any disambiguating ` (email)` suffix. */
export function clientSelectDisplayName(label: string): string {
  return label.replace(/\s*\([^()]*\)\s*$/, '').trim() || label;
}
